import { MessageBarType } from '@fluentui/react';
import { useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppInstance } from '../../ai.h2o.cloud.appstore';
import { envURL } from '../../services/api';
import { useInstance } from '../../utils/hooks';
import { getInstanceAppTitleVersion, handleErrMsg } from '../../utils/utils';
import InstanceLogPage, { InstanceLogType } from '../InstanceLogPage/InstanceLogPage';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../Routes';

export interface AppInstanceLogPageProps {
  instanceId: string;
}

function AppInstanceLogPage({ instanceId }: AppInstanceLogPageProps) {
  const [appInstance, setAppInstance] = useState<AppInstance | null>(null),
    { pathname, state } = useLocation<{ parent?: string }>(),
    isAdminPage = pathname.includes('/admin'),
    { getInstance, getAdminInstance } = useInstance(),
    getInstanceEndpoint = isAdminPage ? getAdminInstance : getInstance,
    { ACTIVE_WORKSPACE_NAME } = useWorkspaces(),
    parent = state?.parent || ACTIVE_WORKSPACE_NAME,
    [websocketUrl, setWebsocketUrl] = useState<URL>(),
    // utilities
    { addToast } = useToast(),
    // callbacks
    fetchInstance = useCallback(async () => {
      try {
        const instance = await getInstanceEndpoint(instanceId, parent, true);
        setAppInstance(instance);
      } catch (error: any) {
        addToast({
          messageBarType: MessageBarType.error,
          message: `An error occurred while loading the app instance: ${handleErrMsg(error.message as string)}`,
        });
      }
    }, [instanceId, parent, getInstanceEndpoint, addToast]);

  // effects
  useEffect(() => {
    const adminPath = RoutePaths.ADMIN_INSTANCELOG.split(':')[0];
    const pathname = window.location.pathname.includes(adminPath)
      ? `/v1/adminlog/${instanceId}`
      : `/v1/log/${instanceId}`;
    setWebsocketUrl(new URL(`${envURL || window.location.origin}${pathname}`));
  }, [instanceId]);
  useEffect(() => {
    void fetchInstance();
  }, [instanceId, getInstance]);
  const instanceName = getInstanceAppTitleVersion(appInstance);

  return (
    <InstanceLogPage
      instanceType={InstanceLogType.APP}
      instanceName={instanceName}
      instanceId={instanceId}
      enablePreviousLog={true}
      websocketUrl={websocketUrl}
      instance={appInstance || undefined}
      instanceParent={parent}
    />
  );
}

export default AppInstanceLogPage;
